import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, MapPin, Send, CheckCircle, 
  Facebook, Instagram, Youtube,
  User, Briefcase, Calculator, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = 'https://api.mt-productions.co.uk/api';

// Team members data
const teamMembers = [
  {
    name: "Mark Thompson",
    role: "MD",
    email: "mark.thompson@mt-productions.co.uk",
    department: "Leadership"
  },
  {
    name: "Marc Featherstone",
    role: "Head of Production - Theatre",
    email: "marc.featherstone@mt-productions.co.uk",
    department: "Production"
  },
  {
    name: "Mike Goble",
    role: "Project Manager",
    email: "mike.goble@mt-productions.co.uk",
    department: "Production"
  },
  {
    name: "Emma Anderson",
    role: "Creative Manager",
    email: "emma.anderson@mt-productions.co.uk",
    department: "Creative"
  },
  {
    name: "Brenda Langley",
    role: "Finance Manager",
    email: "brenda.langley@mt-productions.co.uk",
    department: "Finance"
  },
  {
    name: "Esther Featherstone",
    role: "Administration Manager",
    email: "esther.featherstone@mt-productions.co.uk",
    department: "Administration"
  }
];

const departmentEmails = [
  {
    department: "Invoicing Queries",
    email: "finance@mt-productions.co.uk",
    icon: Calculator
  },
  {
    department: "Schools Enquiries",
    email: "schools@ministryofscience.org",
    icon: GraduationCap
  }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubjectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      subject: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Execute reCAPTCHA v3 to get a token
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (!window.grecaptcha || !siteKey) {
        throw new Error('reCAPTCHA failed to load. Please refresh and try again.');
      }

      const recaptchaToken = await new Promise((resolve, reject) => {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute(siteKey, { action: 'contact_submit' });
            resolve(token);
          } catch (err) {
            reject(err);
          }
        });
      });

      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptcha_token: recaptchaToken })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit');
      }
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-6 uppercase">
              Get In Touch
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-brand-dark mb-8 leading-tight">
              Contact <span className="text-brand-primary">Us</span>
            </h1>
            <p className="font-body text-gray-600 text-xl">
              Whether you're a venue, partner, or family with questions, we'd love to hear from you
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-gray-50 rounded-3xl p-8 lg:p-10">
                {submitted ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
                      <CheckCircle className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="font-display text-3xl font-bold text-brand-dark mb-4">
                      Message Sent!
                    </h3>
                    <p className="font-body text-gray-600 mb-8 text-lg">
                      Thank you for getting in touch. We'll respond to your enquiry as soon as possible.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', subject: '', message: '' });
                      }}
                      className="px-8 py-4 bg-brand-primary text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-gray-700 mb-3 block font-medium">
                          Your Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          data-testid="contact-name-input"
                          className="h-14 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl text-base"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-gray-700 mb-3 block font-medium">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          data-testid="contact-email-input"
                          className="h-14 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl text-base"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-gray-700 mb-3 block font-medium">
                        Subject *
                      </Label>
                      <Select 
                        value={formData.subject} 
                        onValueChange={handleSubjectChange}
                        required
                      >
                        <SelectTrigger 
                          data-testid="contact-subject-select"
                          className="h-14 bg-white border-gray-200 text-gray-900 rounded-xl"
                        >
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          <SelectItem value="General Enquiry" className="text-gray-900">
                            General Enquiry
                          </SelectItem>
                          <SelectItem value="Venue Booking" className="text-gray-900">
                            Venue Booking
                          </SelectItem>
                          <SelectItem value="Partnership" className="text-gray-900">
                            Partnership Opportunity
                          </SelectItem>
                          <SelectItem value="Press" className="text-gray-900">
                            Press / Media
                          </SelectItem>
                          <SelectItem value="Other" className="text-gray-900">
                            Other
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-700 mb-3 block font-medium">
                        Your Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        data-testid="contact-message-input"
                        className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 resize-none rounded-xl text-base p-4"
                        placeholder="Tell us how we can help..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                      className="w-full h-14 bg-accent-gold text-brand-dark rounded-xl font-bold text-lg transition-all duration-300 hover:bg-brand-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    >
                      {submitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-8"
            >
              {/* Info Cards */}
              <div className="grid gap-6">
                <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-7 h-7 text-brand-primary" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-brand-dark mb-2">
                        Email Us
                      </h3>
                      <a
                        href="mailto:info@mt-productions.co.uk"
                        className="font-body text-gray-600 hover:text-brand-primary transition-colors text-lg"
                      >
                        info@mt-productions.co.uk
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-7 h-7 text-brand-primary" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-brand-dark mb-2">
                        Visit Us
                      </h3>
                      <address className="font-body text-gray-600 not-italic text-lg leading-relaxed">
                        Unit 5 & Unit 11, Pontnewynydd Small Business Centre<br />
                        Pontnewynydd, Pontypool<br />
                        Torfaen, NP4 6AD
                      </address>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="bg-brand-dark rounded-2xl p-8">
                <h3 className="font-display text-xl font-bold text-white mb-4">
                  Follow Us
                </h3>
                <p className="font-body text-white/70 mb-6">
                  Stay connected for behind-the-scenes content, show announcements, and exclusive offers.
                </p>
                <div className="flex gap-4">
                  {[
                    { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/MarkThompsonProductionsLTD' },
                    { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/mark_Thompson_productions/' },
                    { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/channel/UCSojjkCbvRneTHkVyIIM76A' },
                  ].map(({ icon: Icon, label, href }) => (
                    <a
                      key={label}
                      href={href}
                      data-testid={`contact-social-${label.toLowerCase()}`}
                      className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white/70 hover:bg-accent-gold hover:text-brand-dark transition-all duration-300"
                      aria-label={label}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team / Departments Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-4 uppercase">
              Our Team
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-brand-dark mb-6">
              Direct <span className="text-brand-primary">Contacts</span>
            </h2>
            <p className="font-body text-gray-600 text-lg max-w-2xl mx-auto">
              Get in touch with the right person for your enquiry
            </p>
          </motion.div>

          {/* Team Members Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.email}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <User className="w-6 h-6 text-brand-primary" />
                </div>
                <h3 className="font-display text-lg font-bold text-brand-dark mb-1">
                  {member.name}
                </h3>
                <p className="font-body text-brand-primary text-sm font-medium mb-3">
                  {member.role}
                </p>
                <a
                  href={`mailto:${member.email}`}
                  className="font-body text-gray-600 text-sm hover:text-brand-primary transition-colors break-all"
                >
                  {member.email}
                </a>
              </motion.div>
            ))}
          </div>

          {/* Department Emails */}
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {departmentEmails.map((dept, index) => (
              <motion.a
                key={dept.email}
                href={`mailto:${dept.email}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-brand-dark rounded-2xl p-6 hover:bg-brand-primary transition-all duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <dept.icon className="w-6 h-6 text-accent-gold" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-white mb-1">
                      {dept.department}
                    </h3>
                    <p className="font-body text-white/70 text-sm group-hover:text-white/90 transition-colors">
                      {dept.email}
                    </p>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
